import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { ArrowLeft, UserPlus, History } from "lucide-react";
import { toast } from "sonner";
import { API, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import StatusBadge from "../components/StatusBadge";
import InfoTip from "../components/InfoTip";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Skeleton } from "@/components/ui/skeleton";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";

function InfoRow({ label, value, mono, tip }) {
  return (
    <div className="flex items-start justify-between gap-4 py-2 border-b border-border/50 last:border-0">
      <span className="text-xs font-semibold uppercase tracking-wide text-muted-foreground flex items-center gap-1">
        {label} {tip && <InfoTip text={tip} testid={`tip-${label.toLowerCase().replace(/\s|\/|'/g, "-")}`} />}
      </span>
      <span className={`text-sm text-right ${mono ? "mono" : ""}`}>{value ?? "-"}</span>
    </div>
  );
}

export default function VehicleDetail() {
  const { id } = useParams();
  const { hasPerm } = useAuth();
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [assignOpen, setAssignOpen] = useState(false);
  const [driverQuery, setDriverQuery] = useState("");
  const [driverOptions, setDriverOptions] = useState([]);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [saving, setSaving] = useState(false);

  const load = () => {
    API.get(`/vehicles/${id}`).then((r) => setData(r.data)).catch((e) => setError(formatApiError(e)));
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [id]);

  useEffect(() => {
    if (driverQuery.trim().length < 2) { setDriverOptions([]); return; }
    const t = setTimeout(() => {
      API.get("/drivers", { params: { q: driverQuery, page_size: 8 } })
        .then((r) => setDriverOptions(r.data.items))
        .catch(() => {});
    }, 300);
    return () => clearTimeout(t);
  }, [driverQuery]);

  const assign = async () => {
    if (!selectedDriver) return;
    setSaving(true);
    try {
      await API.post(`/vehicles/${id}/assignments`, { person_id: selectedDriver.id, role: "conducteur" });
      toast.success(`${selectedDriver.nom} ${selectedDriver.prenom} est desormais conducteur de ce vehicule.`);
      setAssignOpen(false);
      setSelectedDriver(null);
      setDriverQuery("");
      load();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (error) return <div className="rounded-lg border border-red-200 bg-red-50 dark:bg-red-900/20 p-6 text-red-700" data-testid="vehicle-error">{error}</div>;
  if (!data) return <div className="space-y-4"><Skeleton className="h-10 w-64" /><Skeleton className="h-64 w-full" /></div>;

  const v = data.vehicle;

  return (
    <div className="space-y-6" data-testid="vehicle-detail-page">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" asChild data-testid="back-to-vehicles">
            <Link to="/vehicules"><ArrowLeft className="h-4 w-4" /></Link>
          </Button>
          <div>
            <nav className="text-xs text-muted-foreground mb-1"><Link to="/vehicules" className="hover:underline">Vehicules</Link> / {v.immatriculation}</nav>
            <h1 className="text-2xl sm:text-3xl font-heading font-bold mono" data-testid="vehicle-immat">{v.immatriculation}</h1>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <StatusBadge value={v.statut} testid="vehicle-status" />
          {hasPerm("UPDATE_VEHICLE") && (
            <Button onClick={() => setAssignOpen(true)} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="vehicle-assign">
              <UserPlus className="h-4 w-4 mr-1" /> Affecter un conducteur
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card data-testid="vehicle-info">
          <CardHeader><CardTitle className="text-base font-heading">Caracteristiques</CardTitle></CardHeader>
          <CardContent>
            <InfoRow label="Immatriculation" value={v.immatriculation} mono />
            <InfoRow label="VIN / Chassis" value={v.vin} mono tip="Numero d'identification unique du vehicule (17 caracteres)." />
            <InfoRow label="Marque" value={v.marque} />
            <InfoRow label="Modele" value={v.modele} />
            <InfoRow label="Type" value={v.type} />
            <InfoRow label="Couleur" value={v.couleur} />
            <InfoRow label="Annee" value={v.annee} />
            <InfoRow label="Mise en circulation" value={v.date_mec} />
          </CardContent>
        </Card>

        <Card data-testid="vehicle-ownership">
          <CardHeader><CardTitle className="text-base font-heading">Propriete et exploitation</CardTitle></CardHeader>
          <CardContent>
            <InfoRow label="Proprietaire" value={v.proprietaire_nom} />
            <InfoRow label="Structure" value={data.organization?.nom} />
            <InfoRow label="Statut" value={v.statut} />
            <InfoRow label="Cree le" value={v.created_at ? new Date(v.created_at).toLocaleDateString("fr-FR") : "-"} />
            <InfoRow label="Version" value={v.version} mono />
          </CardContent>
        </Card>
      </div>

      <Card data-testid="vehicle-history">
        <CardHeader>
          <CardTitle className="text-base font-heading flex items-center gap-2">
            <History className="h-4 w-4 text-[#3A75C4]" /> Historique des affectations
          </CardTitle>
        </CardHeader>
        <CardContent>
          {data.assignments.length === 0 ? (
            <p className="text-sm text-muted-foreground">Aucune affectation enregistree.</p>
          ) : (
            <div className="overflow-x-auto rounded-lg border">
              <table className="w-full text-sm">
                <thead className="bg-accent/60 text-left">
                  <tr>{["Conducteur", "Structure", "Role", "Debut", "Fin", "Statut"].map((h) => (
                    <th key={h} className="px-3 py-2.5 text-xs font-semibold uppercase tracking-wide text-muted-foreground">{h}</th>
                  ))}</tr>
                </thead>
                <tbody>
                  {data.assignments.map((a, i) => (
                    <tr key={i} className="border-t hover:bg-accent/40" data-testid={`assignment-row-${i}`}>
                      <td className="px-3 py-2.5 font-medium">
                        {a.person_id ? <Link to={`/conducteurs/${a.person_id}`} className="text-[#3A75C4] hover:underline">{a.conducteur}</Link> : a.conducteur}
                      </td>
                      <td className="px-3 py-2.5 text-xs">{a.structure || "-"}</td>
                      <td className="px-3 py-2.5 text-xs">{a.role}</td>
                      <td className="px-3 py-2.5 text-xs">{a.start_date}</td>
                      <td className="px-3 py-2.5 text-xs">{a.end_date || "-"}</td>
                      <td className="px-3 py-2.5">
                        {a.actuel
                          ? <StatusBadge value="ACTIF" testid={`assignment-current-${i}`} />
                          : <StatusBadge value="INACTIF" />}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={assignOpen} onOpenChange={setAssignOpen}>
        <DialogContent data-testid="assign-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">Affecter un conducteur — {v.immatriculation}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <Input value={driverQuery} onChange={(e) => { setDriverQuery(e.target.value); setSelectedDriver(null); }}
              placeholder="Rechercher un conducteur (nom, telephone...)" data-testid="assign-search" />
            {driverOptions.length > 0 && !selectedDriver && (
              <div className="rounded-lg border divide-y max-h-56 overflow-y-auto">
                {driverOptions.map((d) => (
                  <button key={d.id} type="button" onClick={() => setSelectedDriver(d)}
                    className="w-full text-left px-3 py-2 text-sm hover:bg-accent transition-colors"
                    data-testid={`assign-option-${d.id}`}>
                    <b>{d.nom}</b> {d.prenom} <span className="text-xs text-muted-foreground mono">{d.telephone}</span>
                  </button>
                ))}
              </div>
            )}
            {selectedDriver && (
              <p className="rounded-lg bg-[#009E60]/10 border border-[#009E60]/30 px-3 py-2 text-sm" data-testid="assign-selected">
                Conducteur selectionne : <b>{selectedDriver.nom} {selectedDriver.prenom}</b>
              </p>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAssignOpen(false)} data-testid="assign-cancel">Annuler</Button>
            <Button onClick={assign} disabled={!selectedDriver || saving} className="bg-[#009E60] hover:bg-[#00804e] text-white" data-testid="assign-confirm">
              {saving ? "Affectation..." : "Confirmer l'affectation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
